import {
	type SketchEntityReferenceRecord,
	type SketchReferenceStatus,
} from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import {InMemorySketchEntityReferenceStore} from './InMemorySketchEntityReferenceStore';
import type {
	SketchEntityReferenceTopologyOwnerQueryInput,
	SketchEntityReferenceRepositoryWriteScope,
	SketchEntityReferenceRepository,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRepository';

export type {
	SketchEntityReferenceTopologyOwnerQueryInput,
	SketchEntityReferenceRepositoryWriteScope,
	SketchEntityReferenceRepository,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRepository';

/**
 * 内存版草图实体引用仓库。
 *
 * 仓库继承基础存储，只额外负责事务边界、topology owner 查询和历史同步入口。
 */
export class InMemorySketchEntityReferenceRepository
	extends InMemorySketchEntityReferenceStore
	implements SketchEntityReferenceRepository {
	/** 显式写事务队列中最后一个任务的完成信号。 */
	private writeQueueTail: Promise<void> = Promise.resolve();

	/**
	 * 将写任务追加到 FIFO 队列，并保留本次任务的真实返回值或异常。
	 *
	 * 队列尾会把成功和失败都转换为完成信号，避免一次失败阻塞后续事务。
	 *
	 * @param work 需要串行执行的写任务。
	 * @returns 本次写任务的结果 Promise。
	 */
	private enqueueWrite<T>(work: () => Promise<T>): Promise<T> {
		const result = this.writeQueueTail.then(work);
		this.writeQueueTail = result.then(
			() => undefined,
			() => undefined,
		);
		return result;
	}

	/**
	 * 在独立工作区中执行写入，成功后一次性提交完整状态。
	 *
	 * 回调同步或异步失败时不会执行提交，因此正式仓库自然保留事务前状态。
	 *
	 * @param work 只接触事务工作区的写入工作。
	 * @param isCommitCurrent 可选的提交所有权校验；在正式快照替换前同步执行。
	 * @param afterCommit 正式快照替换后同步执行的不可逆收尾。
	 * @returns 回调返回结果。
	 */
	public withWriteTransaction<T>(
		work: (tx: SketchEntityReferenceRepositoryWriteScope) => Promise<T>,
		isCommitCurrent?: () => boolean,
		afterCommit?: () => void,
	): Promise<T> {
		return this.enqueueWrite(async () => {
			const beforeScope = new InMemorySketchEntityReferenceStore();
			const transactionScope = new InMemorySketchEntityReferenceStore();

			// 同时冻结正式 before-image；post-commit 若异常，仓储仍能恢复唯一事实源。
			this.copyReferencesState(this, beforeScope);
			this.copyReferencesState(beforeScope, transactionScope);
			const result = await work(transactionScope);

			/** 校验与正式快照复制之间没有异步让出点，形成文档代次安全的唯一提交边界。 */
			if (isCommitCurrent && !isCommitCurrent()) {
				throw new Error('草图实体引用写事务已过期，已放弃提交');
			}
			// 只有回调完整成功后才替换正式状态，形成清晰的唯一提交点。
			this.copyReferencesState(transactionScope, this);
			try {
				afterCommit?.();
			} catch (error) {
				this.copyReferencesState(beforeScope, this);
				throw error;
			}
			return result;
		});
	}

	/**
	 * 同步恢复单条引用记录，供 history adapter 在 PatchApplier 同步栈内使用。
	 *
	 * @param reference 需要恢复的引用记录。
	 */
	public restoreReferenceSync(reference: SketchEntityReferenceRecord): void {
		this.saveReferencesImmediately([reference]);
	}

	/**
	 * 同步读取单条引用记录快照。
	 *
	 * @param referenceId 引用记录 ID。
	 * @returns 命中时返回深拷贝记录，否则返回 null。
	 */
	public loadReferenceSync(referenceId: string): SketchEntityReferenceRecord | null {
		return this.loadReferenceImmediately(referenceId);
	}

	/**
	 * 同步删除单条引用记录，供 history adapter 在 PatchApplier 同步栈内使用。
	 *
	 * @param referenceId 引用记录 ID。
	 */
	public deleteReferenceSync(referenceId: string): void {
		this.removeReferencesImmediately([referenceId]);
	}

}
